import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Banknote, ClipboardCheck, Layers3, ReceiptText, WalletCards } from "lucide-react";

import OpenWorkQueueCards from "../../components/OpenWorkQueueCards";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  SearchInput,
  SkeletonRows,
  Surface,
} from "../../../components/ui";
import { Pagination } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { formatCents, formatDate, titleCase } from "./financeUtils";
import { getPOLabel } from "../../../utils/documentLabels";
import ShopSelect from "../../../components/ShopSelect";

// Four operational rows keep the complete queue frame, including pagination,
// visible at common desktop heights without introducing page-level scrolling.
const PAGE_SIZE = 4;
const TODAY = new Date().toISOString().slice(0, 10);

const PRIORITY_ORDER = {
  urgent: 0,
  high: 1,
  blocked: 2,
  normal: 3,
};

const PRIORITY_TONES = {
  urgent: "danger",
  high: "warning",
  blocked: "danger",
  normal: "neutral",
};

function isOverdue(row) {
  const status = String(row?.statusDisplay || row?.status || "").toLowerCase();
  const dueDate = String(row?.dueDate || "").slice(0, 10);
  return status === "overdue" || Boolean(dueDate && dueDate < TODAY);
}

function payrollAction(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "released") return "Settle payroll";
  if (["submitted", "pending_finance", "finance_review"].includes(normalized)) return "Review payroll";
  return "Continue payroll";
}

export default function FinanceOpenWork() {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [payables, setPayables] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [payroll, setPayroll] = useState({ periods: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [priority, setPriority] = useState("all");
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [pendingRes, payablesRes, receivablesRes, payrollRes] = await Promise.all([
        financeAPI.listPending(),
        financeAPI.listPayables({ status: "open", limit: 100 }),
        financeAPI.listReceivables({ status: "open", limit: 100 }),
        financeAPI.getPayroll(),
      ]);
      setPendingApprovals(Array.isArray(pendingRes?.data) ? pendingRes.data : []);
      setPayables(Array.isArray(payablesRes?.items) ? payablesRes.items : []);
      setReceivables(Array.isArray(receivablesRes?.items) ? receivablesRes.items : []);
      setPayroll({ periods: Array.isArray(payrollRes?.data?.periods) ? payrollRes.data.periods : [] });
    } catch {
      setLoadError(true);
      setPendingApprovals([]);
      setPayables([]);
      setReceivables([]);
      setPayroll({ periods: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const actionQueue = useMemo(() => {
    const approvalActions = pendingApprovals.map((row) => ({
      key: `approval-${row.id}`,
      category: "approvals",
      categoryLabel: "Approval",
      priority: "high",
      title: getPOLabel(row),
      detail: `${row.supplier_name || row.supplier_company || "Supplier"} · ${row.product_name || "Product"} · Qty ${row.quantity || 0}`,
      amountCents: Number(row.unit_price_cents || 0) * Number(row.quantity || 0),
      timing: "Awaiting finance review",
      actionLabel: "Review",
      href: "/business/finance/approvals",
    }));

    const payableActions = payables.map((row) => {
      const overdue = isOverdue(row);
      const dueDate = row.dueDate || row.createdAt;
      return {
        key: `payable-${row.rowKey || row.id}`,
        category: "payables",
        categoryLabel: "Payable",
        priority: overdue ? "urgent" : "normal",
        title: row.title || "Payable obligation",
        detail: row.counterpartyName || "Counterparty",
        amountCents: Number(row.amountRemainingCents || 0),
        timing: overdue ? `Overdue · ${formatDate(dueDate)}` : `Due ${formatDate(dueDate)}`,
        actionLabel: "Open payable",
        href: "/business/finance/payables",
      };
    });

    const receivableActions = receivables.map((row) => {
      const overdue = isOverdue(row);
      const dueDate = row.dueDate || row.createdAt;
      return {
        key: `receivable-${row.rowKey || row.id}`,
        category: "receivables",
        categoryLabel: "Receivable",
        priority: overdue ? "urgent" : "normal",
        title: row.title || "Receivable obligation",
        detail: row.counterpartyName || "Counterparty",
        amountCents: Number(row.amountRemainingCents || 0),
        timing: overdue ? `Overdue · ${formatDate(dueDate)}` : `Due ${formatDate(dueDate)}`,
        actionLabel: "Open receivable",
        href: "/business/finance/receivables",
      };
    });

    const payrollActions = payroll.periods
      .filter((row) => !["settled", "cancelled"].includes(String(row.status || "").toLowerCase()))
      .map((row) => {
        const status = String(row.status || "").toLowerCase();
        return {
          key: `payroll-${row.id}`,
          category: "payroll",
          categoryLabel: "Payroll",
          priority: ["submitted", "pending_finance", "finance_review", "released"].includes(status) ? "high" : "normal",
          title: row.name || `${formatDate(row.start_date)} - ${formatDate(row.end_date)}`,
          detail: `${titleCase(row.status)} · Pay date ${row.pay_date ? formatDate(row.pay_date) : "not set"}`,
          amountCents: Number(row.payroll_open_cents || row.net_pay_cents || 0),
          timing: payrollAction(status),
          actionLabel: payrollAction(status),
          href: status === "released" ? `/business/finance/payroll/payments?period=${row.id}` : "/business/finance/payroll",
        };
      });

    return [...approvalActions, ...payableActions, ...receivableActions, ...payrollActions]
      .sort((left, right) => PRIORITY_ORDER[left.priority] - PRIORITY_ORDER[right.priority]);
  }, [payables, payroll.periods, pendingApprovals, receivables]);

  const payrollCount = actionQueue.filter((item) => item.category === "payroll").length;
  const urgentCount = actionQueue.filter((item) => item.priority === "urgent").length;
  const reviewCount = actionQueue.filter((item) => item.category === "approvals" || item.actionLabel === "Review payroll").length;
  const lanes = [
    { id: "approvals", title: "Approvals", description: "Awaiting finance review", actionLabel: "Review approvals", icon: ClipboardCheck, tone: "warning", count: pendingApprovals.length },
    { id: "payables", title: "Payables", description: "Open payable obligations", actionLabel: "Review payables", icon: WalletCards, tone: payables.some(isOverdue) ? "danger" : "warning", count: payables.length },
    { id: "receivables", title: "Receivables", description: "Open receivable obligations", actionLabel: "Review receivables", icon: ReceiptText, tone: receivables.some(isOverdue) ? "danger" : "warning", count: receivables.length },
    { id: "payroll", title: "Payroll", description: "Payroll actions still unresolved", actionLabel: "Review payroll", icon: Banknote, tone: "warning", count: payrollCount },
    { id: "all", title: "All Work", description: "Every unresolved finance action", actionLabel: "Review all work", icon: Layers3, tone: "accent", count: actionQueue.length },
  ];
  const requestedLane = searchParams.get("lane");
  const category = lanes.some((item) => item.id === requestedLane)
    ? requestedLane
    : lanes.find((item) => item.id !== "all" && item.count > 0)?.id ?? "all";
  const activeLaneLabel = lanes.find((item) => item.id === category)?.title || "All Work";

  const filteredActions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return actionQueue.filter((item) => {
      const matchesQuery = !normalizedQuery || [item.title, item.detail, item.categoryLabel, item.timing]
        .some((value) => String(value || "").toLowerCase().includes(normalizedQuery));
      const matchesCategory = category === "all" || item.category === category;
      const matchesPriority = priority === "all" || item.priority === priority;
      return matchesQuery && matchesCategory && matchesPriority;
    });
  }, [actionQueue, category, priority, query]);

  const totalPages = Math.max(1, Math.ceil(filteredActions.length / PAGE_SIZE));
  const visibleActions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredActions.slice(start, start + PAGE_SIZE);
  }, [filteredActions, page]);

  useEffect(() => {
    setPage(1);
  }, [category, priority, query]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Finance Open Work"
        description="Prioritize and resolve pending finance decisions, overdue obligations, and payroll actions."
      />

      <OpenWorkQueueCards
        queues={lanes}
        active={category}
        loading={loading}
        onChange={(nextLane) => setSearchParams({ lane: nextLane })}
        idPrefix="finance-queue"
        panelId="finance-work-panel"
        label="Finance work queues"
      />

      <Surface
        as="section"
        id="finance-work-panel"
        role="tabpanel"
        aria-labelledby={`finance-queue-${category}`}
        toolbar={(
          <div className="space-y-3">
            <div>
              <h2 className="text-section-title text-foreground">{category === "all" ? "Prioritized Finance Action Queue" : activeLaneLabel}</h2>
              <p className="mt-1 text-body-sm text-secondary">Every row represents unresolved work and routes to the module where the action is completed.</p>
              <p className="mt-1 text-meta text-tertiary">
                {loading ? "…" : urgentCount} urgent or overdue · {loading ? "…" : reviewCount} waiting for review
              </p>
            </div>
            <div className="grid gap-2 md:grid-cols-[minmax(280px,1fr)_190px]">
              <SearchInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                label="Search finance open work"
                placeholder="Search work items, parties, or actions..."
              />
              <ShopSelect
                value={priority}
                onValueChange={setPriority}
                options={[
                  { value: "all", label: "All priorities" },
                  { value: "urgent", label: "Urgent / overdue" },
                  { value: "high", label: "High priority" },
                  { value: "blocked", label: "Blocked" },
                  { value: "normal", label: "Normal" },
                ]}
                ariaLabel="Filter finance work by priority"
                align="end"
                minPanelWidth={190}
                triggerClassName="min-h-control w-full rounded-control px-3 text-control"
                panelClassName="py-1"
              />
            </div>
          </div>
        )}
      >
        {loading ? <SkeletonRows rows={5} columns={4} label="Loading finance work" /> : null}
        {!loading && loadError ? (
          <ErrorState title="Could not load finance work" description="Please try again." onRetry={load} />
        ) : null}
        {!loading && !loadError && !filteredActions.length ? (
          <EmptyState
            title={actionQueue.length ? "No work matches these filters" : "Finance is caught up"}
            description={actionQueue.length ? "Adjust the search or filters to see other finance work." : "There are no unresolved approvals, obligations, or payroll actions."}
          />
        ) : null}
        {!loading && !loadError && filteredActions.length ? (
          <div>
            <ul className="divide-y divide-subtle">
              {visibleActions.map((item) => (
                <li key={item.key} className="flex min-h-touch flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-label text-foreground">{item.title}</span>
                      <Badge tone={PRIORITY_TONES[item.priority]}>{titleCase(item.priority)}</Badge>
                      <span className="text-meta text-tertiary">{item.categoryLabel}</span>
                    </div>
                    <p className="mt-1 text-body-sm text-secondary">{item.detail}</p>
                    <p className="mt-1 text-body-sm text-foreground">{item.timing}</p>
                  </div>
                  <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:shrink-0 sm:justify-end">
                    <span className="text-money tabular-nums text-foreground">{item.amountCents == null ? "—" : formatCents(item.amountCents)}</span>
                    <Button
                      as={Link}
                      to={item.href}
                      size="sm"
                      variant="secondary"
                      className="border-accent/30 bg-accent-subtle/40 text-accent hover:border-accent hover:bg-accent-subtle"
                    >
                      {item.actionLabel}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex min-h-14 flex-wrap items-center justify-between gap-2 border-t border-subtle px-4 py-3">
              <span className="text-meta text-tertiary">
                Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredActions.length)} of {filteredActions.length} open items
              </span>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        ) : null}
      </Surface>
    </div>
  );
}
